import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, Smartphone, Share, PlusSquare, Bell, ArrowRight, Clock } from "lucide-react";
import { Logo, Blason } from "@/components/Logo";
import { isStandalone, isIOS, canPromptInstall, onInstallAvailable, promptInstall, saveJoin, loadJoin, setNotifOptIn } from "@/lib/pwa";
import { isPushSupported } from "@/lib/push";

const STATUS_TXT = { pending: "En attente de validation par votre dirigeant", accepted: "Accès validé — vous pouvez vous connecter", refused: "Demande refusée — contactez votre entreprise" };

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="w-full max-w-sm animate-fade-in">{children}</div>
    </div>
  );
}

// Étape 2 : installation de la PWA (masquée si déjà en mode application)
function InstallStep({ onNext }) {
  const [available, setAvailable] = useState(canPromptInstall());
  const [busy, setBusy] = useState(false);
  useEffect(() => onInstallAvailable(setAvailable), []);
  const ios = isIOS();
  const install = async () => {
    setBusy(true);
    const r = await promptInstall();
    setBusy(false);
    if (r === "accepted") onNext();
  };
  return (
    <Shell>
      <div className="flex justify-center mb-5"><Blason size={140} /></div>
      <h2 data-testid="install-title" className="text-2xl font-heading font-bold text-brand-900 text-center">Installez SanitDocs sur votre téléphone</h2>
      <p className="text-slate-500 mt-3 text-center">Pour recevoir vos demandes et vos notifications directement sur votre téléphone, installez SanitDocs comme une application.</p>
      {ios || !available ? (
        <ol data-testid="ios-install-steps" className="mt-6 space-y-3 bg-white border border-slate-200 rounded-2xl p-4">
          <li className="flex items-center gap-3 text-slate-800 font-semibold"><span className="w-7 h-7 rounded-full bg-accent-50 text-accent-800 text-sm flex items-center justify-center shrink-0">1</span><Share className="w-5 h-5 text-accent-700 shrink-0" />Appuyez sur <b>Partager</b></li>
          <li className="flex items-center gap-3 text-slate-800 font-semibold"><span className="w-7 h-7 rounded-full bg-accent-50 text-accent-800 text-sm flex items-center justify-center shrink-0">2</span><PlusSquare className="w-5 h-5 text-accent-700 shrink-0" />Choisissez <b>Sur l'écran d'accueil</b></li>
          <li className="flex items-center gap-3 text-slate-800 font-semibold"><span className="w-7 h-7 rounded-full bg-accent-50 text-accent-800 text-sm flex items-center justify-center shrink-0">3</span><CheckCircle2 className="w-5 h-5 text-accent-700 shrink-0" />Confirmez avec <b>Ajouter</b></li>
          {!ios && <p className="text-xs text-slate-400 pt-1">Sur Android : menu ⋮ du navigateur → « Installer l'application » ou « Ajouter à l'écran d'accueil ».</p>}
        </ol>
      ) : (
        <Button data-testid="install-btn" onClick={install} disabled={busy} className="mt-6 h-12 w-full bg-brand-800 hover:bg-brand-900 text-base font-bold">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}Installer SanitDocs
        </Button>
      )}
      <Button data-testid="install-continue" variant="outline" onClick={onNext} className="mt-3 h-12 w-full font-bold">Continuer <ArrowRight className="w-4 h-4 ml-2" /></Button>
      <p className="text-xs text-slate-400 text-center mt-4">Votre demande d'accès est déjà enregistrée : vous n'aurez rien à ressaisir.</p>
    </Shell>
  );
}

// Étape 3 : autorisation des notifications (jamais demandée à l'ouverture du QR code)
function NotifStep({ onNext }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const ask = async () => {
    setBusy(true);
    try {
      try { await navigator.serviceWorker?.register("/sw.js"); } catch (_) {}
      const p = await new Promise((res) => { const r = Notification.requestPermission((x) => res(x)); if (r?.then) r.then(res, () => res(Notification.permission)); });
      setState(p);
      if (p === "granted") { setNotifOptIn(true); onNext(); }
    } finally { setBusy(false); }
  };
  return (
    <Shell>
      <div className="w-16 h-16 rounded-2xl bg-accent-600 flex items-center justify-center mx-auto mb-5"><Bell className="w-8 h-8 text-white" /></div>
      <h2 data-testid="notif-title" className="text-2xl font-heading font-bold text-brand-900 text-center">Activer les notifications</h2>
      <p className="text-slate-500 mt-3 text-center">SanitDocs pourra vous prévenir lorsqu'un document doit être récupéré ou lorsqu'une demande vous est affectée.</p>
      {state === "denied" && <p data-testid="notif-denied" className="mt-4 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">Notifications bloquées : vous pourrez les autoriser plus tard dans les réglages de votre téléphone.</p>}
      {state !== "denied" && state !== "unsupported" && (
        <Button data-testid="notif-enable" onClick={ask} disabled={busy} className="mt-6 h-12 w-full bg-accent-600 hover:bg-accent-700 text-base font-bold">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}Activer les notifications
        </Button>
      )}
      <Button data-testid="notif-later" variant="outline" onClick={onNext} className="mt-3 h-12 w-full font-bold">Plus tard</Button>
    </Shell>
  );
}

function DoneStep({ join }) {
  const [status, setStatus] = useState(join?.status || "pending");
  useEffect(() => {
    if (!join?.email || !join?.code) return;
    api.get("/access-requests/status", { params: { code: join.code, email: join.email } }).then((r) => setStatus(r.data.status)).catch(() => {});
  }, [join]);
  return (
    <Shell>
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        {status === "accepted" ? <CheckCircle2 className="w-8 h-8 text-green-600" /> : <Clock className="w-8 h-8 text-green-600" />}
      </div>
      <h2 data-testid="join-done-title" className="text-2xl font-heading font-bold text-brand-900 text-center">{status === "accepted" ? "Accès validé" : "Demande envoyée"}</h2>
      <p className="text-slate-500 mt-2 text-center">Votre demande d'accès à <b>{join?.organization_name}</b> {status === "accepted" ? "a été acceptée." : "a été transmise. Un dirigeant doit la valider avant votre première connexion."}</p>
      <p data-testid="join-status" className="mt-3 text-sm font-semibold text-center text-accent-800 bg-accent-50 border border-accent-200 rounded-lg px-3 py-2">{STATUS_TXT[status] || status}</p>
      {isStandalone() && <p className="text-xs text-slate-400 text-center mt-3">SanitDocs est installé : ouvrez-le depuis l'icône de votre téléphone une fois l'accès validé.</p>}
      <Link to="/login"><Button className="mt-6 h-12 w-full bg-accent-600 hover:bg-accent-700" data-testid="back-to-login">{status === "accepted" ? "Se connecter" : "Aller à la connexion"}</Button></Link>
    </Shell>
  );
}

export default function JoinPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const saved = loadJoin();
  const restored = params.get("joined") === "1" && (saved || params.get("email"));
  const [f, setF] = useState({ company_code: (params.get("code") || "").toUpperCase(), first_name: "", last_name: "", phone: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [join, setJoin] = useState(restored ? (saved || { code: params.get("code"), email: params.get("email"), organization_name: params.get("org") || "" }) : null);
  const [step, setStep] = useState(restored ? (params.get("step") || "done") : "form");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const goAfterJoin = (data) => {
    setJoin(data);
    // L'URL porte l'état : si le téléphone installe la PWA depuis cette page, rien n'est perdu.
    const q = new URLSearchParams({ joined: "1", code: data.code, email: data.email, org: data.organization_name, step: "done" });
    window.history.replaceState({}, "", `/rejoindre?${q}`);
    setStep(isStandalone() ? (isPushSupported() ? "notif" : "done") : "install");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/access-requests", f);
      const j = { code: f.company_code.toUpperCase().trim(), email: f.email.toLowerCase().trim(), first_name: f.first_name, last_name: f.last_name,
                  organization_name: data.organization_name, request_id: data.request_id || null, status: "pending" };
      saveJoin(j);
      goAfterJoin(j);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (step === "install") return <InstallStep onNext={() => setStep(isPushSupported() || isIOS() ? "notif" : "done")} />;
  if (step === "notif") return <NotifStep onNext={() => setStep("done")} />;
  if (step === "done") return <DoneStep join={join} />;

  return (
    <Shell>
      <div className="mb-6"><Logo size={40} tagline /></div>
      <h2 className="text-2xl font-heading font-bold text-brand-900">Rejoindre une entreprise</h2>
      <p className="text-slate-500 mt-1 mb-6 text-sm">Saisissez le code fourni par votre entreprise (ex : BOURBON).</p>
      {saved && saved.code && (
        <button type="button" data-testid="resume-join" onClick={() => { setJoin(saved); setStep("done"); }} className="w-full text-left mb-4 text-sm bg-accent-50 border border-accent-200 rounded-xl px-3 py-2.5 text-accent-800 font-semibold">
          Une demande existe déjà pour <b>{saved.organization_name}</b> ({saved.email}). Voir son statut →
        </button>
      )}
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Code entreprise</Label><Input data-testid="join-code" value={f.company_code} onChange={set("company_code")} className="mt-1 h-12 uppercase" required /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom</Label><Input data-testid="join-firstname" value={f.first_name} onChange={set("first_name")} className="mt-1 h-12" required /></div>
          <div><Label>Nom</Label><Input data-testid="join-lastname" value={f.last_name} onChange={set("last_name")} className="mt-1 h-12" required /></div>
        </div>
        <div><Label>Téléphone</Label><Input data-testid="join-phone" value={f.phone} onChange={set("phone")} className="mt-1 h-12" /></div>
        <div><Label>Email</Label><Input type="email" name="email" autoComplete="email" data-testid="join-email" value={f.email} onChange={set("email")} className="mt-1 h-12" required /></div>
        <div><Label>Mot de passe</Label><Input type="password" name="password" autoComplete="new-password" data-testid="join-password" value={f.password} onChange={set("password")} className="mt-1 h-12" required /></div>
        {error && <p data-testid="join-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <Button type="submit" data-testid="join-submit" disabled={loading} className="w-full h-12 bg-accent-600 hover:bg-accent-700">
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Envoyer ma demande
        </Button>
      </form>
      <Link to="/login" className="block text-center text-sm text-accent-700 mt-4 hover:underline">Retour à la connexion</Link>
    </Shell>
  );
}
